import React, { FC, memo, ReactNode } from 'react';
import styled from '@emotion/styled';
import ItemImage from '../../../Components/ItemImage';
import GlassTag from '../../../Components/GlassPill/GlassTag';
import AllergenList from './AllergenList';

/**
 * Roughly the pane's CSS width on a landscape kiosk - ItemImage doubles this
 * for retina on its own, so this must stay the display width, not the final
 * CDN width, or the photo is requested at 2x the size it needs to be.
 */
const HERO_IMAGE_WIDTH = 800;

/**
 * Full-bleed photo header, edge to edge and cropped to a fixed band, with the
 * title, price and description set in white over a dark gradient along its
 * bottom edge - the product page pattern shoppers know from food apps. The
 * band is a share of the screen height, so it never towers in landscape and
 * never shrinks to a stamp in portrait.
 */
const Hero = styled.header`
  position: relative;
  width: 100%;
  height: clamp(220px, 34vh, 400px);
  overflow: hidden;
  background: var(--eposPrimary);
  color: var(--colorWhite);

  @media (orientation: landscape) {
    height: clamp(200px, 38vh, 340px);
  }
`;

/**
 * The 3:2 image, full width and vertically centered, so the band crops it
 * evenly. On the step pages it blurs into a soft backdrop - frosted enough for
 * the centered text to read, not so much that the food disappears - and scales
 * up a touch so the blur's soft edges stay outside the band.
 */
const Photo = styled(ItemImage)`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  transition: filter 0.4s ease-out, transform 0.4s ease-out;

  .is-step & {
    filter: blur(14px) brightness(0.85);
    transform: translateY(-50%) scale(1.08);
  }
`;

/** Bottom-heavy so the text stays readable on light photos while the top stays clear. */
const Scrim = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.35) 0%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0.25) 55%,
    rgba(0, 0, 0, 0.82) 100%
  );

  transition: opacity 0.4s ease-out;

  /* Even tint over the blurred photo - the text sits in the middle now, not along the bottom. */
  .is-step & {
    opacity: 0;
  }
`;

/** Second layer that fades in for step mode, since a gradient can't transition into a flat tint. */
const StepScrim = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.4s ease-out;

  .is-step & {
    opacity: 1;
  }
`;

/** Text block on the left, price tag on the right, the tag centered vertically against the whole block. */
const Text = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 36px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  /* Step pages: title and description centered in the band, clear of the pills above and the progress below. */
  .is-step & {
    top: 0;
    padding: 72px 36px 110px;
    justify-content: center;
    text-align: center;
  }
`;

/**
 * The hero is one element across pages, so the layout jump between the two
 * modes is covered by fading the copy out and in at its new place. Two
 * differently named animations so the switch restarts it in either direction.
 */
const Copy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: eposHeroCopyBottom 0.45s ease-out both;

  .is-step & {
    align-items: center;
    animation-name: eposHeroCopyCenter;
  }

  @keyframes eposHeroCopyBottom {
    from {
      opacity: 0;
      transform: translate3d(0, 10px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes eposHeroCopyCenter {
    from {
      opacity: 0;
      transform: translate3d(0, 24px, 0) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0) scale(1);
    }
  }
`;

/** Shared entrance for the pills that only exist in step mode. */
const stepEnter = `
  animation: eposHeroStepEnter 0.4s ease-out 0.1s both;

  @keyframes eposHeroStepEnter {
    from {
      opacity: 0;
      transform: translate3d(0, 8px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }
`;

/** Step pages: allergens pinned top right, mirroring the back pill in the other corner. */
const AllergenCorner = styled.div`
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 1;
  ${stepEnter}
`;

/**
 * Step pages: the progress row along the bottom of the band. The row's own
 * right-edge fade is painted in the pane color, which would show as a block
 * over the photo, so it is turned off here.
 */
const ProgressSlot = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 36px 14px;
  ${stepEnter}

  [data-testid='epos-step-progress']::after {
    display: none;
  }
`;

const Title = styled.h2`
  margin: 0;
  min-width: 0;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.8px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
`;

/** The glass tag from the header, so the price reads as a fact about the photo, not a button. */
const Price = styled(GlassTag)`
  flex: 0 0 auto;
`;

/** Clamped to three lines - the full text is rarely needed, the photo is the pitch. */
const Description = styled.p`
  margin: 0;
  max-width: 62ch;
  font-size: 15px;
  line-height: 1.4;
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

interface Props {
  title: string;
  description: string;
  /** Formatted base price. Omit to hide the tag - on the step pages, where the total lives in the add button. */
  price?: string;
  imageUrl: string;
  /** Shown as a glass strip - under the description, or top right in step mode. Nothing rendered when empty. */
  allergyTags?: string[];
  /**
   * Step mode, for the pages after the details: blurred photo, title and
   * description centered, allergens in the top-right corner and `progress`
   * (the StepProgress row) along the bottom edge.
   */
  progress?: ReactNode;
}

const ItemHero: FC<Props> = ({
  title,
  description,
  price,
  imageUrl,
  allergyTags = [],
  progress,
}) => {
  const isStep = !!progress;
  const allergens = allergyTags.length > 0 && <AllergenList tags={allergyTags} onPhoto />;

  return (
    <Hero data-testid="epos-item-hero" className={isStep ? 'is-step' : undefined}>
      <Photo imageUrl={imageUrl} cdnWidth={HERO_IMAGE_WIDTH} borderRadius={0} />
      <Scrim />
      <StepScrim />
      {isStep && allergens && <AllergenCorner>{allergens}</AllergenCorner>}
      <Text>
        <Copy>
          <Title>{title}</Title>
          {!!description && <Description>{description}</Description>}
          {!isStep && allergens}
        </Copy>
        {!!price && <Price>{price}</Price>}
      </Text>
      {isStep && <ProgressSlot>{progress}</ProgressSlot>}
    </Hero>
  );
};

export default memo(ItemHero);
